import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import cx from "classnames";
import Calendar from "./Calendar/Calendar";
import styles from "./MarkDateOffHoliday.module.css";

import { getUser } from "../../helpers/sessionStorageHelper";
import {
  fetchHolidayDates,
  fetchSpecialScheduleDates,
  // updateSpecialSchedule,
} from "../../dux/bookNow";
import { getBookNowDataWithDateObject } from "../../dux/bookNow";
import { isApiPending } from "../../dux/apiStatus";

const Tabs = [
  { title: "MARK HOLIDAYS", key: 1 },
  { title: "MARK OFF DAYS", key: 2 },
];

// 🆕 Location-wise sport availability
const locationSportsMap = {
  srinagar: ["Football", "Box Cricket", "Cricket Net", "Pickle Ball"],
  jammu: ["Pickle Ball"],
  // baramulla: ["Pickle Ball"],
};

// Map frontend sport names to backend sport names
const sportMapping = {
  Football: "football",
  "Box Cricket": "box-cricket",
  "Cricket Net": "cricket-net",
  "Pickle Ball": "pickle-ball",
};

function getAllowedLocations() {
  const user = getUser();
  return user && user.locations ? user.locations : [];
}

const MarkDateOffHoliday = () => {
  const dispatch = useDispatch();
  const [type, setType] = useState(1);

  const allowedLocations = getAllowedLocations();
  const [selectedLocation, setSelectedLocation] = useState(allowedLocations[0]);

  // Redux state
  const { holidayDates, specialScheduleDates } = useSelector(
    getBookNowDataWithDateObject
  );
  const fetchingHolidayDates = useSelector((state) =>
    isApiPending(state, "fetchHolidayDates")
  );
  const fetchingSpecialScheduleDates = useSelector((state) =>
    isApiPending(state, "fetchSpecialScheduleDates")
  );

  const clickHandler = (key) => {
    setType(key);
  };

  const handleLocationChange = (e) => {
    const newLocation = e.target.value;
    setSelectedLocation(newLocation);
    // No need to navigate, just update state
  };

  const sportsList = locationSportsMap[selectedLocation?.toLowerCase()] || [];
  const [selectedSport, setSelectedSport] = useState(sportsList[0]);
  const locationDisplay = {
    srinagar: "Srinagar",
    // baramulla: "Baramulla",
  };

  useEffect(() => {
    // When selectedLocation changes, update selectedSport to the first available sport
    const sportsList = locationSportsMap[selectedLocation?.toLowerCase()] || [];
    if (sportsList.length > 0) {
      setSelectedSport(sportsList[0]);
    }
  }, [selectedLocation]);

  useEffect(() => {
    // Fetch dates when sport or location changes
    if (selectedSport && selectedLocation) {
      if (type === 1) {
        // For "Mark Holidays" - fetch special schedule dates
        dispatch(
          fetchSpecialScheduleDates({
            sport: sportMapping[selectedSport],
            location: selectedLocation,
          })
        );
      } else {
        // For "Mark Off Days" - fetch holiday dates
        dispatch(
          fetchHolidayDates({
            sport: sportMapping[selectedSport],
            location: selectedLocation,
          })
        );
      }
    }
  }, [selectedSport, selectedLocation, type, dispatch]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.calendarContainer}>
          <div className={styles.sportContainer}>
            <h3 className={styles.heading3}>Location:</h3>
            <select
              className={styles.inputField}
              value={selectedLocation}
              onChange={handleLocationChange}
            >
              {allowedLocations.map((loc) => (
                <option key={loc} value={loc}>
                  {locationDisplay[loc] || loc}
                </option>
              ))}
            </select>
            <h3 className={styles.heading3}>Select Sports:</h3>
            <div className={styles.sportsCards}>
              {sportsList.includes("Football") && (
                <button
                  onClick={() => setSelectedSport("Football")}
                  className={cx(styles.sportCard, {
                    [styles.selectedSport]: selectedSport === "Football",
                  })}
                >
                  <div className={styles.footballSportCard} />
                  <span>Football</span>
                </button>
              )}
              {sportsList.includes("Box Cricket") && (
                <button
                  onClick={() => setSelectedSport("Box Cricket")}
                  className={cx(styles.sportCard, {
                    [styles.selectedSport]: selectedSport === "Box Cricket",
                  })}
                >
                  <div className={styles.cricketSportCard} />
                  <span>Box Cricket</span>
                </button>
              )}
              {sportsList.includes("Cricket Net") && (
                <button
                  onClick={() => setSelectedSport("Cricket Net")}
                  className={cx(styles.sportCard, {
                    [styles.selectedSport]: selectedSport === "Cricket Net",
                  })}
                >
                  <div className={styles.cricketSportCard} />
                  <span>Cricket Nets</span>
                </button>
              )}
              {sportsList.includes("Pickle Ball") && (
                <button
                  onClick={() => setSelectedSport("Pickle Ball")}
                  className={cx(styles.sportCard, {
                    [styles.selectedSport]: selectedSport === "Pickle Ball",
                  })}
                >
                  <div className={styles.pickeBallSportCard} />
                  <span>Pickleball</span>
                </button>
              )}
            </div>
          </div>
          <h3 className={styles.heading3}>Select Type:</h3>
          <div className={styles.typeContainer}>
            {Tabs.map(({ title, key }) => (
              <div
                key={key}
                className={cx(styles.typeCard, {
                  [styles.selectedType]: type === key,
                })}
                onClick={() => clickHandler(key)}
              >
                {title}
              </div>
            ))}
          </div>
          <h3 className={styles.heading3}>Select Date:</h3>
          <Calendar
            type={type}
            sport={selectedSport}
            location={selectedLocation}
          />

          {/* Display Dates based on type */}
          <div className={styles.holidayDatesContainer}>
            <h3 className={styles.heading3}>
              Current {type === 1 ? "Special Schedule" : "Holiday"} Dates:
            </h3>
            {(
              type === 1 ? fetchingSpecialScheduleDates : fetchingHolidayDates
            ) ? (
              <p>
                Loading {type === 1 ? "special schedule" : "holiday"} dates...
              </p>
            ) : (type === 1 ? specialScheduleDates : holidayDates) &&
              (type === 1 ? specialScheduleDates : holidayDates).length > 0 ? (
              <div className={styles.holidayDatesList}>
                {(type === 1 ? specialScheduleDates : holidayDates).map(
                  (date, index) => (
                    <span key={index} className={styles.holidayDate}>
                      {date}
                    </span>
                  )
                )}
              </div>
            ) : (
              <p>
                No {type === 1 ? "special schedule" : "holiday"} dates
                configured for this sport and location.
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="footer"></div>
    </>
  );
};

export default MarkDateOffHoliday;
